/**
 * Typed wrappers around `/cards*`. Backed by `useApi`.
 *
 * The returned object is memoized on `[api]` so consumers can pass it
 * to `useEffect`/`useCallback` without retriggering on every render.
 */

import { useMemo } from 'react';
import type {
  CustomerPhotoWorkflowStatus,
  PublishCustomerPhotosRequest,
  CardLookupRequest,
  CardLookupResponse,
  ReviewChecklistAssignmentVerification,
  ChecklistSubsetPolicy,
} from '@tcvs/shared';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';
import type {
  CardIdentity,
  CardListingPrice,
  CardRecord,
  ChecklistContextSuggestion,
  ChecklistEntry,
  ChecklistParallelDefinition,
  ChecklistSubsetDefinition,
  ChecklistParallelMatch,
  ChecklistSourceSelection,
  ChecklistSuggestionObservation,
  ChecklistSuggestionStatus,
  ChecklistProductSuggestionFailure,
  ChecklistIssue,
  ChecklistIssueGuidanceResponse,
  CreateChecklistIssueRequest,
  ImageQualityStatus,
  PendingImageReplacement,
  PreliminaryImageReview,
  PreliminaryImageReviewStatus,
  LocationResolution,
  ProductDescriptor,
  ReviewChecklistProduct,
  BatchChecklistResponse,
  BatchParallelSelectionResponse,
  BatchGridBulkEditRequest,
  BatchGridBulkEditResponse,
  RunChecklistResponse,
} from '../shared/types';

export interface CatalogListCardRecord extends CardRecord {
  /** Placement attributes already returned by the catalog read projection. */
  placementState?: 'STORED' | 'PULLED';
  placementUpdatedAt?: string;
  /** Derived across all current batch cards before applying review filters. */
  currentBatchPosition?: number;
  currentBatchSize?: number;
  versionedImageReviewRequired?: boolean;
  imageUrl?: string;
  imageUrls?: { front?: string; back?: string };
  imageUrlExpiresIn?: number;
}
export interface VersionedImageReview {
  cardId: string;
  reviewId: string;
  expiresAt: number;
  imageUrls: { front?: string; back?: string };
}

export interface CatalogListResponse {
  items: CatalogListCardRecord[];
  lastKey: string | null;
}

export interface IdentityReviewGroupSummary {
  key: string;
  batchId?: string;
  runId: string | null;
  count: number;
  latest: number;
  previewCards: Array<{ cardId: string; shortCode?: string }>;
}

export interface IdentityReviewGroupsResponse {
  groups: IdentityReviewGroupSummary[];
}

export interface IdentityReviewPreviewCard {
  cardId: string;
  shortCode?: string;
  imageUrl?: string;
}

/** Filters mirror the API contract (`packages/api/src/handlers/catalog.ts`). */
export interface CatalogFilters {
  q?: string;
  category?: string;
  set?: string;
  name?: string;
  gradeMin?: number;
  gradeMax?: number;
  limit?: number;
  lastKey?: string;
  /**
   * Resolve a single card by its short code. The API short-code branch returns
   * a single {@link CardRecord} (not a list); prefer {@link useCatalogApi}'s
   * `lookupByShortCode` accessor rather than threading this through `list`.
   */
  shortCode?: string;
  /** Exact external-service SKU match (sku-write-back). Returns the list shape. */
  sku?: string;
  /** Exact identity-status filter (card-centric-review-queue); the Review Queue
   *  passes `UNIDENTIFIED`. */
  identityStatus?: string;
  /** Filter on the mirrored batch dismiss-state (card-centric-review-queue): the
   *  default queue passes `false`, the dismissed view `true`. */
  reviewDismissed?: boolean;
  /** Exact image approval status filter for the Image Approval review section. */
  imageQualityStatus?: ImageQualityStatus;
  /** Require a device-captured replacement pair that still awaits a decision. */
  pendingImageReplacement?: boolean;
  /** Exact Storage_Batch membership query used by the identification workbench. */
  placementBatchId?: string;
  /** Exact capture-run membership query used for lazy review-group expansion. */
  runId?: string;
  /** Reviewer-entered identities without checklist lineage. */
  manualIdentityOverrideStatus?: 'DRAFT' | 'VERIFIED';
}

export interface CatalogDetailResponse extends CardRecord {
  identityReviewDigest?: string;
  versionedImageReviewRequired?: boolean;
  imageUrl?: string;
  /** Immediate Storage_Batch membership while the card is stored. */
  placementBatchId?: string;
  /** Per-side presigned GET URLs, keyed by `Side`. Absent sides are omitted. */
  imageUrls?: { front?: string; back?: string };
  imageUrlExpiresIn: number;
  /**
   * The card's resolved physical location, walked from the card → Storage_Batch
   * → Box → Shelf by the Catalog_Service (`GET /cards/{id}`). One of the
   * discriminated `LocationResolution` variants (STORED / STORED_UNPLACED /
   * PULLED / NOT_PLACED).
   */
  location?: LocationResolution;
  pendingImageReplacement?: PendingImageReplacement & {
    imageUrls: { front: string; back: string };
  };
}

type IdentityReviewBase = {
  reviewScope: 'IDENTITY_ONLY';
  mode: 'VERIFY';
  expectedIdentityReviewDigest: string;
  identity: CardIdentity;
};
export type IdentityOnlyReviewRequest = IdentityReviewBase &
  (
    | {
        identityAction: 'RETAIN';
        checklistSourceSelection?: never;
        manualIdentityOverrideReason?: never;
      }
    | ({ identityAction: 'SELECT' } & ReviewChecklistAssignmentVerification &
        (
          | {
              checklistSourceSelection: ChecklistSourceSelection;
              manualIdentityOverrideReason?: never;
            }
          | {
              checklistSourceSelection?: null;
              manualIdentityOverrideReason: string;
              manualIdentityOverrideSourceSelection?: ChecklistSourceSelection;
              manualIdentityOverrideCompleted?: boolean;
            }
        ))
  );
export interface IdentityOnlyReviewResponse {
  cardId: string;
  reviewScope: 'IDENTITY_ONLY';
  identityAction: 'SELECT' | 'RETAIN';
  identityStatus: 'IDENTIFIED';
  identity: CardIdentity;
  checklistSourceLineage: CardRecord['checklistSourceLineage'] | null;
  manualIdentityOverride: CardRecord['manualIdentityOverride'] | null;
  trainingCapture: 'NOT_REQUIRED' | 'RECORDED' | 'UNKNOWN';
  warning?: string;
}
const reviewJson = (value: unknown): string =>
  JSON.stringify(value, (_key, item: unknown) =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)))
      : item,
  );
const reviewObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
/** Validate a confirmed response against the exact submitted intent and saved authority. */
export function identityReviewResponseMatches(
  value: unknown,
  card: CatalogDetailResponse,
  request: IdentityOnlyReviewRequest,
  operatorId?: string,
): value is IdentityOnlyReviewResponse {
  if (
    !reviewObject(value) ||
    value.cardId !== card.cardId ||
    value.reviewScope !== 'IDENTITY_ONLY' ||
    value.identityAction !== request.identityAction ||
    value.identityStatus !== 'IDENTIFIED' ||
    reviewJson(value.identity) !== reviewJson(request.identity) ||
    !['NOT_REQUIRED', 'RECORDED', 'UNKNOWN'].includes(String(value.trainingCapture)) ||
    Object.keys(value).some(
      (key) =>
        ![
          'cardId',
          'reviewScope',
          'identityAction',
          'identityStatus',
          'identity',
          'checklistSourceLineage',
          'manualIdentityOverride',
          'trainingCapture',
          'warning',
        ].includes(key),
    ) ||
    (value.warning !== undefined &&
      (value.trainingCapture !== 'UNKNOWN' || typeof value.warning !== 'string'))
  )
    return false;
  if (request.identityAction === 'RETAIN')
    return (
      reviewJson(value.checklistSourceLineage) ===
        reviewJson(card.checklistSourceLineage ?? null) &&
      reviewJson(value.manualIdentityOverride) ===
        reviewJson(card.manualIdentityOverride ?? null) &&
      value.trainingCapture === 'NOT_REQUIRED'
    );
  if (request.checklistSourceSelection) {
    const source = value.checklistSourceLineage;
    if (
      !reviewObject(source) ||
      source.status !== 'VERIFIED' ||
      typeof source.linkedAt !== 'string' ||
      !Number.isFinite(Date.parse(source.linkedAt)) ||
      typeof source.linkedBy !== 'string' ||
      !source.linkedBy ||
      (operatorId !== undefined && source.linkedBy !== operatorId) ||
      value.manualIdentityOverride !== null
    )
      return false;
    const { status: _status, linkedAt: _at, linkedBy: _by, ...selection } = source;
    return reviewJson(selection) === reviewJson(request.checklistSourceSelection);
  }
  const manual = value.manualIdentityOverride;
  if (
    value.checklistSourceLineage !== null ||
    !reviewObject(manual) ||
    manual.status !== 'VERIFIED' ||
    manual.reason !== request.manualIdentityOverrideReason.trim() ||
    typeof manual.reviewedAt !== 'string' ||
    !Number.isFinite(Date.parse(manual.reviewedAt)) ||
    typeof manual.reviewedBy !== 'string' ||
    !manual.reviewedBy ||
    (operatorId !== undefined && manual.reviewedBy !== operatorId) ||
    Object.keys(manual).some(
      (key) => !['status', 'reason', 'reviewedAt', 'reviewedBy', 'sourceEvidence'].includes(key),
    )
  )
    return false;
  if (!request.manualIdentityOverrideSourceSelection) return manual.sourceEvidence === undefined;
  const original = [
    card.identityReviewDraft?.manualIdentityOverrideSourceEvidence,
    card.manualIdentityOverride?.sourceEvidence,
  ].find(
    (item) =>
      item &&
      reviewJson(item.selection) === reviewJson(request.manualIdentityOverrideSourceSelection),
  );
  return original !== undefined && reviewJson(manual.sourceEvidence) === reviewJson(original);
}

export interface ImageReplacementUploadResponse {
  cardId: string;
  side: 'front' | 'back';
  s3Key: string;
  uploadUrl: { url: string; fields: Record<string, string> };
  expiresIn: number;
}

export interface ManualCardUploadResponse {
  cardId: string;
  batchId: string;
  uploads: {
    front: { s3Key: string; uploadUrl: { url: string; fields: Record<string, string> } };
    back: { s3Key: string; uploadUrl: { url: string; fields: Record<string, string> } };
  };
  expiresIn: number;
}

export interface CompleteManualCardUploadResponse {
  card?: CardRecord;
  batchId: string;
}

/** Convert the typed filter object into the loose query bag accepted by the api-client. */
function filtersToQuery(filters: CatalogFilters): Record<string, string | number | undefined> {
  const out: Record<string, string | number | undefined> = {};
  if (filters.q !== undefined) out['q'] = filters.q;
  if (filters.category !== undefined) out['category'] = filters.category;
  if (filters.set !== undefined) out['set'] = filters.set;
  if (filters.name !== undefined) out['name'] = filters.name;
  if (filters.gradeMin !== undefined) out['gradeMin'] = filters.gradeMin;
  if (filters.gradeMax !== undefined) out['gradeMax'] = filters.gradeMax;
  if (filters.limit !== undefined) out['limit'] = filters.limit;
  if (filters.lastKey !== undefined) out['lastKey'] = filters.lastKey;
  if (filters.shortCode !== undefined) out['shortCode'] = filters.shortCode;
  if (filters.sku !== undefined) out['sku'] = filters.sku;
  if (filters.identityStatus !== undefined) out['identityStatus'] = filters.identityStatus;
  if (filters.reviewDismissed !== undefined) {
    out['reviewDismissed'] = filters.reviewDismissed ? 'true' : 'false';
  }
  if (filters.imageQualityStatus !== undefined) {
    out['imageQualityStatus'] = filters.imageQualityStatus;
  }
  if (filters.pendingImageReplacement !== undefined) {
    out['pendingImageReplacement'] = filters.pendingImageReplacement ? 'true' : 'false';
  }
  if (filters.placementBatchId !== undefined) {
    out['placementBatchId'] = filters.placementBatchId;
  }
  if (filters.runId !== undefined) out['runId'] = filters.runId;
  if (filters.manualIdentityOverrideStatus !== undefined) {
    out['manualIdentityOverrideStatus'] = filters.manualIdentityOverrideStatus;
  }
  return out;
}

export function useCatalogApi() {
  const api = useApi();
  return useMemo(
    () => ({
      list: (filters: CatalogFilters): Promise<ApiResult<CatalogListResponse>> =>
        api.request<CatalogListResponse>('/cards', {
          method: 'GET',
          query: filtersToQuery(filters),
        }),
      /** Complete lightweight queue groups; card rows are loaded only when expanded. */
      identityReviewGroups: (
        reviewDismissed: boolean,
        query?: string,
        includeCompleted = false,
      ): Promise<ApiResult<IdentityReviewGroupsResponse>> =>
        api.request<IdentityReviewGroupsResponse>('/cards', {
          method: 'GET',
          query: {
            identityStatus: 'UNIDENTIFIED',
            reviewDismissed: reviewDismissed ? 'true' : 'false',
            reviewGroups: 'true',
            ...(includeCompleted ? { includeCompletedReviewGroups: 'true' } : {}),
            ...(query === undefined || query.trim() === '' ? {} : { q: query.trim() }),
          },
        }),
      identityReviewPreviews: (
        cardIds: string[],
      ): Promise<ApiResult<{ items: IdentityReviewPreviewCard[] }>> =>
        api.request<{ items: IdentityReviewPreviewCard[] }>('/review-card-previews', {
          method: 'POST',
          body: { cardIds },
        }),
      /**
       * Resolve a single card by its short code. The API returns the matching
       * {@link CardRecord} directly (not a `{ items }` page) on a hit, and
       * `404 short_code_not_found` / `400 short_code_malformed` on a miss or
       * malformed code — both surface as a non-ok {@link ApiResult}.
       */
      lookupByShortCode: (shortCode: string): Promise<ApiResult<CardRecord>> =>
        api.request<CardRecord>('/cards', { method: 'GET', query: { shortCode } }),
      get: (cardId: string): Promise<ApiResult<CatalogDetailResponse>> =>
        api.request<CatalogDetailResponse>(`/cards/${cardId}`, { method: 'GET' }),
      customerPhotoStatus: (cardId: string, signal?: AbortSignal) =>
        api.request<CustomerPhotoWorkflowStatus>(
          `/cards/${encodeURIComponent(cardId)}/customer-photos`,
          { method: 'GET', signal },
        ),
      publishCustomerPhotos: (
        cardId: string,
        request: PublishCustomerPhotosRequest,
        signal?: AbortSignal,
      ) =>
        api.request<CustomerPhotoWorkflowStatus>(
          `/cards/${encodeURIComponent(cardId)}/customer-photos`,
          { method: 'POST', body: { expectedRevision: request.expectedRevision }, signal },
        ),
      verifyIdentity: (
        cardId: string,
        request: IdentityOnlyReviewRequest,
      ): Promise<ApiResult<IdentityOnlyReviewResponse>> => {
        const body: Record<string, unknown> = {
          reviewScope: 'IDENTITY_ONLY',
          mode: 'VERIFY',
          identityAction: request.identityAction,
          expectedIdentityReviewDigest: request.expectedIdentityReviewDigest,
          identity: request.identity,
        };
        if (request.identityAction === 'SELECT') {
          if (request.expectedReviewChecklistSelection)
            body.expectedReviewChecklistSelection = request.expectedReviewChecklistSelection;
          if (request.replaceChecklistAssignment === true) body.replaceChecklistAssignment = true;
          if (request.checklistSourceSelection)
            body.checklistSourceSelection = request.checklistSourceSelection;
          else {
            body.manualIdentityOverrideReason = request.manualIdentityOverrideReason;
            if (request.manualIdentityOverrideSourceSelection)
              body.manualIdentityOverrideSourceSelection =
                request.manualIdentityOverrideSourceSelection;
            if (request.manualIdentityOverrideCompleted !== undefined)
              body.manualIdentityOverrideCompleted = request.manualIdentityOverrideCompleted;
          }
        }
        return api.request<IdentityOnlyReviewResponse>(`/cards/${cardId}`, {
          method: 'PATCH',
          body,
        });
      },
      retire: (
        cardId: string,
        reason: string,
      ): Promise<ApiResult<{ cardId: string; retiredAt: string }>> =>
        api.request<{ cardId: string; retiredAt: string }>(`/cards/${cardId}/retire`, {
          method: 'POST',
          body: { reason },
        }),
      restore: (cardId: string): Promise<ApiResult<{ cardId: string }>> =>
        api.request<{ cardId: string }>(`/cards/${cardId}/restore`, { method: 'POST' }),
      update: (
        cardId: string,
        identity: CardIdentity,
        options?: ReviewChecklistAssignmentVerification & {
          mode?: 'DRAFT' | 'VERIFY' | 'CORRECT';
          identityAction?: 'RETAIN' | 'SELECT';
          correctionReason?: string;
          cardPresentation?: CardRecord['cardPresentation'];
          imageIssue?: boolean;
          photoReviewId?: string;
          checklistSourceSelection?: ChecklistSourceSelection | null;
          manualIdentityOverrideReason?: string;
          manualIdentityOverrideSourceSelection?: ChecklistSourceSelection;
          manualIdentityOverrideCompleted?: boolean;
          listingPrice?: {
            amountMinor: number;
            currency: 'USD';
            researchQuery?: string;
            notes?: string;
          };
        },
      ): Promise<
        ApiResult<{
          cardId: string;
          identityReviewDraft?: CardRecord['identityReviewDraft'];
          checklistSourceLineage?: CardRecord['checklistSourceLineage'] | null;
          manualIdentityOverride?: CardRecord['manualIdentityOverride'] | null;
          listingPrice?: CardListingPrice;
          cardPresentation?: CardRecord['cardPresentation'];
        }>
      > =>
        api.request<{
          cardId: string;
          identityReviewDraft?: CardRecord['identityReviewDraft'];
          checklistSourceLineage?: CardRecord['checklistSourceLineage'] | null;
          manualIdentityOverride?: CardRecord['manualIdentityOverride'] | null;
          listingPrice?: CardListingPrice;
          cardPresentation?: CardRecord['cardPresentation'];
        }>(`/cards/${cardId}`, {
          method: 'PATCH',
          body: { identity, ...options },
        }),
      beginVersionedImageReview: (
        cardId: string,
        purpose: 'FINAL' | 'PRELIMINARY',
      ): Promise<ApiResult<VersionedImageReview>> =>
        api.request<VersionedImageReview>(`/cards/${cardId}/image-quality`, {
          method: 'PATCH',
          body: { mode: 'BEGIN_VERSIONED', purpose },
        }),
      updateImageQuality: (
        cardId: string,
        status: Exclude<ImageQualityStatus, 'PENDING'>,
        rejectedReason?: string,
        photoReviewId?: string,
      ): Promise<
        ApiResult<{
          cardId: string;
          imageQualityStatus: ImageQualityStatus;
          imageQualityReviewedAt: string;
        }>
      > =>
        api.request<{
          cardId: string;
          imageQualityStatus: ImageQualityStatus;
          imageQualityReviewedAt: string;
        }>(`/cards/${cardId}/image-quality`, {
          method: 'PATCH',
          body: {
            status,
            ...(rejectedReason !== undefined ? { rejectedReason } : {}),
            ...(photoReviewId === undefined ? {} : { photoReviewId }),
          },
        }),
      updatePreliminaryImageReview: (
        cardId: string,
        status: PreliminaryImageReviewStatus,
        photoReviewId?: string,
      ): Promise<ApiResult<{ cardId: string; preliminaryImageReview: PreliminaryImageReview }>> =>
        api.request<{ cardId: string; preliminaryImageReview: PreliminaryImageReview }>(
          `/cards/${cardId}/image-quality`,
          {
            method: 'PATCH',
            body: {
              mode: 'PRELIMINARY',
              status,
              ...(photoReviewId === undefined ? {} : { photoReviewId }),
            },
          },
        ),
      updateListingPrice: (
        cardId: string,
        value: {
          amountMinor: number;
          currency: 'USD';
          researchQuery?: string;
          notes?: string;
        } | null,
      ): Promise<ApiResult<{ cardId: string; listingPrice: CardListingPrice | null }>> =>
        api.request<{ cardId: string; listingPrice: CardListingPrice | null }>(
          `/cards/${cardId}/listing-price`,
          {
            method: 'PATCH',
            body: value === null ? { amountMinor: null } : value,
          },
        ),
      updateReviewDismissed: (
        cardId: string,
        dismissed: boolean,
      ): Promise<ApiResult<{ cardId: string; reviewDismissed: boolean }>> =>
        api.request<{ cardId: string; reviewDismissed: boolean }>(
          `/cards/${cardId}/review-dismissed`,
          {
            method: 'PATCH',
            body: { dismissed },
          },
        ),
      createImageReplacementUpload: (
        cardId: string,
        side: 'front' | 'back',
        file: File,
      ): Promise<ApiResult<ImageReplacementUploadResponse>> =>
        api.request<ImageReplacementUploadResponse>(
          `/cards/${cardId}/images/${side}/replacement-upload`,
          {
            method: 'POST',
            body: { contentType: file.type, sizeBytes: file.size },
          },
        ),
      replaceImage: (
        cardId: string,
        side: 'front' | 'back',
        s3Key: string,
      ): Promise<
        ApiResult<{
          cardId: string;
          side: 'front' | 'back';
          imageQualityStatus: ImageQualityStatus;
        }>
      > =>
        api.request<{
          cardId: string;
          side: 'front' | 'back';
          imageQualityStatus: ImageQualityStatus;
        }>(`/cards/${cardId}/images/${side}/replace`, {
          method: 'POST',
          body: { s3Key },
        }),
      decidePendingImageReplacement: (
        cardId: string,
        replacementId: string,
        decision: 'approve' | 'reject',
        reason?: string,
        photoReviewId?: string,
      ): Promise<
        ApiResult<{
          cardId: string;
          replacementId: string;
          decision: 'approve' | 'reject';
          imageQualityStatus: ImageQualityStatus;
        }>
      > =>
        api.request<{
          cardId: string;
          replacementId: string;
          decision: 'approve' | 'reject';
          imageQualityStatus: ImageQualityStatus;
        }>(`/cards/${cardId}/image-quality`, {
          method: 'PATCH',
          body: {
            status: decision === 'approve' ? 'ACCEPTED' : 'REJECTED',
            pendingReplacementId: replacementId,
            ...(photoReviewId === undefined ? {} : { photoReviewId }),
            ...(reason === undefined ? {} : { rejectedReason: reason }),
          },
        }),
      createManualCardUpload: (
        batchId: string,
        front: File,
        back: File,
      ): Promise<ApiResult<ManualCardUploadResponse>> =>
        api.request<ManualCardUploadResponse>('/cards/manual-upload', {
          method: 'POST',
          body: {
            batchId,
            front: { contentType: imageContentType(front), sizeBytes: front.size },
            back: { contentType: imageContentType(back), sizeBytes: back.size },
          },
        }),
      completeManualCardUpload: (
        cardId: string,
        batchId: string,
        images: { front: string; back: string },
      ): Promise<ApiResult<CompleteManualCardUploadResponse>> =>
        api.request<CompleteManualCardUploadResponse>(`/cards/manual-upload/${cardId}/complete`, {
          method: 'POST',
          body: { batchId, images },
        }),
      /**
       * Resolve a card against the manufacturer checklist at Review time
       * (checklist-reference). `POST /cards/{id}/checklist-resolve` looks the
       * card up by its `(product, number)` anchor and returns one of:
       *   - a single unambiguous match — `{ match, checklistSuggestion }`; the
       *     server has already written `checklistSuggestion` on the card, so the
       *     caller refreshes to surface it (the suggestion block + pre-fill);
       *   - a Parallel menu — `{ candidates }` (two or more entries), written
       *     nowhere, for the operator to choose from;
       *   - no match / no usable number — `{ candidates: [] }`, no write.
       * Best-effort and non-authoritative: a non-ok result simply leaves the
       * card without a checklist pre-fill.
       */
      checklistResolve: (
        cardId: string,
        options?: {
          checklistId?: string;
          /** String persists a per-card Review override; null clears it. */
          reviewChecklistId?: string | null;
          /** Resolve a tentative OCR-ranked product without writing card state. */
          tentativeChecklist?: boolean;
          variation?: string;
          subset?: string;
          browseSubsets?: boolean;
          searchNumber?: string;
          suggestProductFromOcr?: boolean;
          refreshProductSuggestion?: boolean;
        },
      ): Promise<ApiResult<ChecklistResolveResponse>> =>
        api.request<ChecklistResolveResponse>(`/cards/${cardId}/checklist-resolve`, {
          method: 'POST',
          body: options,
        }),
      /** Read-only search across imported checklist roster entries. */
      checklistCardSearch: (input: CardLookupRequest): Promise<ApiResult<CardLookupResponse>> =>
        api.request<CardLookupResponse>('/review-checklists', {
          method: 'POST',
          body: input,
        }),
      /** Compact directory of checklist products available for Review runs. */
      reviewChecklists: (options?: {
        query?: string;
        limit?: number;
        nextToken?: string;
        checklistId?: string;
      }): Promise<ApiResult<ReviewChecklistsResponse>> =>
        api.request<ReviewChecklistsResponse>('/review-checklists', {
          method: 'GET',
          query: {
            ...(options?.query === undefined ? {} : { q: options.query }),
            ...(options?.limit === undefined ? {} : { limit: String(options.limit) }),
            ...(options?.nextToken === undefined ? {} : { nextToken: options.nextToken }),
            ...(options?.checklistId === undefined ? {} : { checklistId: options.checklistId }),
          },
        }),
      /** Report a source/checklist defect without changing card identity. */
      reportChecklistIssue: (
        issue: CreateChecklistIssueRequest,
      ): Promise<ApiResult<{ issue: ChecklistIssue }>> =>
        api.request<{ issue: ChecklistIssue }>('/checklists/issues', {
          method: 'POST',
          body: issue,
        }),
      /** Read reviewer-safe guidance for the selected checklist and card context. */
      checklistIssueGuidance: (context: {
        checklistId: string;
        cardId?: string;
        cardNumber?: string;
      }): Promise<ApiResult<ChecklistIssueGuidanceResponse>> =>
        api.request<ChecklistIssueGuidanceResponse>('/checklists/issues', {
          method: 'GET',
          query: {
            view: 'guidance',
            checklistId: context.checklistId,
            ...(context.cardId === undefined ? {} : { cardId: context.cardId }),
            ...(context.cardNumber === undefined ? {} : { cardNumber: context.cardNumber }),
          },
        }),
      /** Read the checklist constraint previously assigned to a Capture_Run. */
      getRunChecklist: (runId: string): Promise<ApiResult<RunChecklistResponse>> =>
        api.request<RunChecklistResponse>(`/review-runs/${encodeURIComponent(runId)}/checklist`, {
          method: 'GET',
        }),
      /** Save a selected checklist and resolve the current unreviewed run cards. */
      applyRunChecklist: (
        runId: string,
        checklistId: string,
      ): Promise<ApiResult<RunChecklistResponse>> =>
        api.request<RunChecklistResponse>(`/review-runs/${encodeURIComponent(runId)}/checklist`, {
          method: 'PUT',
          body: { checklistId },
        }),
      /** Rank imported checklist products from OCR sampled across a Capture_Run. */
      suggestRunChecklist: (
        runId: string,
        refresh = false,
      ): Promise<ApiResult<RunChecklistResponse>> =>
        api.request<RunChecklistResponse>(
          `/review-runs/${encodeURIComponent(runId)}/checklist-suggestion`,
          { method: 'POST', query: refresh ? { refresh: 'true' } : undefined },
        ),
      /** Read the checklist constraint previously assigned to a Storage_Batch. */
      getBatchChecklist: (batchId: string): Promise<ApiResult<BatchChecklistResponse>> =>
        api.request<BatchChecklistResponse>(
          `/review-batches/${encodeURIComponent(batchId)}/checklist`,
          { method: 'GET' },
        ),
      /** Save a selected checklist and resolve current unreviewed batch cards. */
      applyBatchChecklist: (
        batchId: string,
        checklistId: string,
        preview = false,
      ): Promise<ApiResult<BatchChecklistResponse>> =>
        api.request<BatchChecklistResponse>(
          `/review-batches/${encodeURIComponent(batchId)}/checklist`,
          {
            method: 'PUT',
            body: { checklistId, ...(preview ? { preview: true } : {}) },
          },
        ),
      /** Rank imported checklist products from OCR sampled across a Storage_Batch. */
      suggestBatchChecklist: (
        batchId: string,
        refresh = false,
      ): Promise<ApiResult<BatchChecklistResponse>> =>
        api.request<BatchChecklistResponse>(
          `/review-batches/${encodeURIComponent(batchId)}/checklist-suggestion`,
          { method: 'POST', query: refresh ? { refresh: 'true' } : undefined },
        ),
      /** Apply one exact checklist variation to selected cards as a suggestion only. */
      applyBatchParallel: (
        batchId: string,
        cardIds: string[],
        variation: string,
      ): Promise<ApiResult<BatchParallelSelectionResponse>> =>
        api.request<BatchParallelSelectionResponse>(
          `/review-batches/${encodeURIComponent(batchId)}/parallel`,
          { method: 'PUT', body: { cardIds, variation } },
        ),
      bulkEditBatch: (
        batchId: string,
        request: BatchGridBulkEditRequest,
      ): Promise<ApiResult<BatchGridBulkEditResponse>> =>
        api.request<BatchGridBulkEditResponse>(
          `/review-batches/${encodeURIComponent(batchId)}/bulk-edit`,
          { method: 'POST', body: request },
        ),
    }),
    [api],
  );
}

function imageContentType(file: File): string {
  if (file.type !== '') return file.type;
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  return '';
}

/**
 * Response of `POST /cards/{id}/checklist-resolve` (checklist-reference). The
 * three resolver outcomes map onto these optional fields:
 *   - single match  → `match` + `checklistSuggestion` (the value written on the
 *     card, echoed back);
 *   - Parallel menu  → `candidates` with two or more entries and, when all
 *     candidates agree, a shared partial `checklistSuggestion`;
 *   - no match       → `candidates: []`.
 */
export interface ChecklistResolveResponse {
  /** Exact selected no-row context advice only; never an authority token. */
  subsetPolicy?: ChecklistSubsetPolicy;
  /** Actual persisted selection revision after an explicit selector change/reset. */
  reviewChecklistSelection?: CardRecord['reviewChecklistSelection'] | null;
  /** The single, unambiguous matching entry (single-match case only). */
  match?: CardIdentity | ChecklistEntry;
  /** The single match or shared identity fields written onto the card. */
  checklistSuggestion?: CardIdentity;
  /** Exact imported source record persisted when a single match is available. */
  checklistSourceLineage?: CardRecord['checklistSourceLineage'] | null;
  /** The matching Parallel entries: 2+ for a menu, empty when nothing matched. */
  candidates?: ChecklistEntry[];
  /** Sparse source definitions that may apply to the returned roster candidates. */
  parallelDefinitions?: ChecklistParallelDefinition[];
  /** Imported subset definitions returned when the operator browses a checklist. */
  subsetDefinitions?: ChecklistSubsetDefinition[];
  /** Advisory source-match tier. Operator submission is still required to verify identity. */
  parallelMatch?: ChecklistParallelMatch;
  /** Whether the resolver wrote a non-authoritative checklist suggestion. */
  written?: boolean;
  /** True when tolerant reconciliation found more than one plausible product. */
  ambiguous?: boolean;
  /** Candidate groups for an ambiguous cross-product checklist lookup. */
  groups?: ChecklistResolveGroup[];
  /** Card-level imported-product hint generated from this card's stored OCR. */
  productSuggestion?: ChecklistContextSuggestion;
  /** Outcome of an explicitly requested card OCR product match. */
  productSuggestionStatus?: ChecklistSuggestionStatus;
  /** Durable bounded reason the latest product suggestion did not match. */
  productSuggestionFailure?: ChecklistProductSuggestionFailure;
  /** Strongest printed-number evidence retained when the selected roster has no matching row. */
  evidenceNumber?: string;
  /** Recognized card OCR evidence when no imported product qualified. */
  observation?: ChecklistSuggestionObservation;
}

export interface ChecklistResolveGroup {
  product: ProductDescriptor;
  candidates: ChecklistEntry[];
}

/** Response of the compact Review checklist directory endpoint. */
export interface ReviewChecklistsResponse {
  items: ReviewChecklistProduct[];
  truncated: boolean;
  nextToken?: string;
}
